<script setup>
import { computed, onMounted, ref } from 'vue';
import Checkbox from '../../common/form/checkbox/Checkbox.vue';
import GenericModal from '../../common/modal/GenericModal.vue';
import KgrButton from '../../common/form/button/KgrButton.vue';
import Offering from '../../../js/dto/offering/Offering.js';
import OfferingBooking from '../../../js/dto/offering/OfferingBooking.js';
import OfferingBookingRepository from '../../../js/repository/club/offering/OfferingBookingRepository.js';
import PricePerTimeOutput from '../../club/sales-list/PricePerTimeOutput.vue';
import { useI18n } from '@/composables/i18n';
import { useNotifications } from '@/composables/notifications';

const { offering, offeringBooking, showDialog } = defineProps({
    offering: {
        type: Offering,
        required: true,
    },
    offeringBooking: {
        type: OfferingBooking,
        required: true,
    },
    showDialog: {
        type: Boolean,
        required: true,
    },
});

const emit = defineEmits(['closeDialog', 'update:offeringBooking']);
const { notify } = useNotifications();
const { i18n } = useI18n();

const mergedOfferingItems = ref([]);
const tHeadline = computed(() => `${i18n.t('offering.editBooking')} - ${offeringBooking.member.firstName} ${offeringBooking.member.lastName}`);

function buildMergedItems() {
    const bookedItems = offeringBooking.bookedSalesListOfferingItems || [];

    // map all salesListOfferingItems with selectedSalesListOfferingItems
    const fromOffering = offering.salesListOfferingItems.map((offeringItem) => {
        const bookedItem = bookedItems.find(
            (selected) => selected.clubFeeId === offeringItem.clubFeeId,
        );

        return {
            clubFeeId: offeringItem.clubFeeId,
            clubFeeName: offeringItem.clubFeeName,
            pricePerTime: offeringItem.pricePerTime,
            memberClubFeeId: bookedItem?.memberClubFeeId ?? null,
            numerator: bookedItem?.numerator ?? 1,
            denominator: bookedItem?.denominator ?? 1,
            isSelected: bookedItem !== undefined,
            isAvailable: bookedItem?.isAvailable ?? true,
            isBilled: bookedItem?.isBilled ?? false,
            maxNumerator: null,
        };
    });

    // add selected items that are no longer in the offering
    const offeringClubFeeIds = offering.salesListOfferingItems.map((item) => item.clubFeeId);
    const orphanedItems = bookedItems
        .filter((selected) => !offeringClubFeeIds.includes(selected.clubFeeId))
        .map((selected) => ({
            clubFeeId: selected.clubFeeId,
            clubFeeName: selected.clubFeeName,
            pricePerTime: selected.pricePerTime,
            memberClubFeeId: selected.memberClubFeeId,
            numerator: selected.numerator,
            denominator: selected.denominator,
            isSelected: true,
            isAvailable: selected.isAvailable,
            isBilled: selected.isBilled,
            maxNumerator: selected.numerator,
        }));

    return [...fromOffering, ...orphanedItems].sort((a, b) => a.clubFeeName.localeCompare(b.clubFeeName));
}

onMounted(() => {
    mergedOfferingItems.value = buildMergedItems();
});

function addOrRemove(item) {
    const existingIndex = mergedOfferingItems.value.findIndex(
        (i) => i.clubFeeId === item.clubFeeId,
    );
    if (existingIndex !== -1) {
        mergedOfferingItems.value[existingIndex].isSelected = !mergedOfferingItems.value[existingIndex].isSelected;
    }
}

function updateQuantity(item, quantity) {
    const existingIndex = mergedOfferingItems.value.findIndex(
        (i) => i.clubFeeId === item.clubFeeId,
    );
    if (existingIndex !== -1) {
        mergedOfferingItems.value[existingIndex].numerator = quantity;
    }
}

function save() {
    const bookedItems = mergedOfferingItems.value
        .filter((item) => item.isSelected)
        .map((item) => ({
            memberClubFeeId: item.memberClubFeeId,
            clubFeeId: item.clubFeeId,
            numerator: item.numerator,
            denominator: item.denominator,
        }));

    OfferingBookingRepository.update(offeringBooking.id, bookedItems)
        .then(() => {
            notify.success('offering.editBooking.succeeded');
            emit('update:offeringBooking');
        })
        .catch(() => notify.error('saveChanges.error'))
        .finally(() => closeDialog());
}

function closeDialog() {
    mergedOfferingItems.value = [];
    emit('closeDialog');
}
</script>

<template>
    <generic-modal
        v-if="showDialog"
        :value="showDialog"
        :headline="tHeadline"
        :width-in-px="800"
        scrollable
        @post-input="closeDialog()"
    >
        <table
            vue:is="kgr-table"
            class="kgr-table kgr-table--striped kgr-table--block"
            :is-data-table="false"
        >
            <thead>
                <tr class="kgr-table__row">
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                        sortable
                    >
                        {{ $t('Name') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                    >
                        {{ $t('Preis') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline kgr-table__cell--center"
                    >
                        {{ $t('Anzahl') }}
                    </th>
                    <th
                        vue:is="kgr-th"
                        class="kgr-table__cell kgr-table__cell--headline"
                    ></th>
                </tr>
            </thead>
            <tbody>
                <tr
                    v-for="item in mergedOfferingItems"
                    :key="item.clubFeeId"
                    class="kgr-table_row"
                >
                    <td class="kgr-table__cell">
                        {{ item.clubFeeName }}
                        <span
                            v-if="!item.isAvailable"
                            class="kgr-badge kgr-ml1"
                        >
                            {{ $t('Nicht mehr verfügbar') }}
                        </span>
                        <span
                            v-if="item.isBilled"
                            class="kgr-badge kgr-ml1"
                        >
                            {{ $t('Abgerechnet') }}
                        </span>
                    </td>
                    <td class="kgr-table__cell">
                        <price-per-time-output
                            :price="item.pricePerTime"
                            with-vat
                        />
                    </td>
                    <td class="kgr-table__cell kgr-table__cell--center">
                        <input
                            type="number"
                            min="1"
                            :max="item.maxNumerator"
                            step="1"
                            :value="item.numerator"
                            :disabled="!item.isSelected || item.isBilled"
                            class="quantity-input"
                            @input="updateQuantity(item, parseInt($event.target.value) || 1)"
                        />
                    </td>
                    <td class="kgr-table__cell">
                        <checkbox
                            :checked="item.isSelected"
                            :readonly="item.isBilled"
                            @change="addOrRemove(item)"
                        />
                    </td>
                </tr>
            </tbody>
        </table>
        <template #footer>
            <kgr-button
                class="kgr-col kgr-button--outline float-left"
                @click="closeDialog"
            >
                {{ $t('cancel') }}
            </kgr-button>
            <kgr-button
                class="kgr-col"
                @click="save()"
            >
                {{ $t('saveAndClose') }}
            </kgr-button>
        </template>
    </generic-modal>
</template>

<style scoped lang="scss">
.quantity-input {
    width: 60px;
    padding: 4px 8px;
    text-align: center;
    border: 1px solid #ccc;
    border-radius: 4px;

    &:disabled {
        background-color: #f5f5f5;
        cursor: not-allowed;
        opacity: 0.6;
    }
}
</style>
